import { LitElement, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { isMobile, isEmpty } from '@fil-global/shared/src/util';
import componentStyles from './fil-hero.styles.js';

/**
 * @tag fil-hero
 * @property {"primary"|"secondary"} [appearance="primary"]
 * @property {string} [image] URL to a background image
 * @property {string} [smallImage] URL to a background image with small device
 * @property {string} [title] Headline
 * @property {boolean} [hasCards]
 * @property {"homepage"|"page"} [variation="page"] Appearance of the component
 */
export class FilHero extends LitElement {
  static properties = {
    appearance: { type: String }, // 'primary' | 'secondary'
    image: { type: String },
    title: { type: String },
    variation: { type: String }, // 'homepage' | 'page'
    hasCards: { type: Boolean },
    smallImage: { type: String },
    _usedImage: { type: String, state: true },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.appearance = 'primary';
    this.variation = 'page';
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    super.disconnectedCallback();
  }

  handleResize() {
    const imageContainer = this.shadowRoot.querySelector('.fil-hero__image');

    if (!isEmpty(this.image)) {
      if (!isMobile()) {
        this._usedImage = this.image;
      } else {
        this._usedImage = this.smallImage ? this.smallImage : this.image;
      }
      imageContainer.style.backgroundImage = `url(${this._usedImage})`;
    }
  }

  get slottedCardGroup() {
    return this.shadowRoot
      .querySelector('slot[name="card-group"]')
      .assignedElements({ flatten: true });
  }

  updated(changedProperties) {
    if (this.appearance !== 'primary' && this.slottedCardGroup.length > 0) {
      this.slottedCardGroup.forEach(child => {
        child.setAttribute('appearance', this.appearance);
      });
    }

    super.updated(changedProperties);
  }

  renderGrid(content) {
    return html` <div class="grid-container position-relative">
      <div class="grid-x">${content}</div>
    </div>`;
  }

  renderSlot() {
    const isHeroPage = this.variation === 'page';

    // If it is a hero homepage and no cards, just return nothing;
    if (!isHeroPage && !this.hasCards) {
      return nothing;
    }

    // Page Hero
    if (isHeroPage) {
      const classes = {
        [`fil-hero-intro-wrapper--${this.appearance}`]: this.appearance !== 'primary',
        'fil-hero-intro-wrapper--no-image': isEmpty(this.image),
      };

      const content = html` <div class="cell large-10 fil-hero-intro-wrapper${classMap(classes)}">
        <div class="fil-hero-intro">
          ${this.title ? html`<h1 class="fil-hero-intro__title">${this.title}</h1>` : nothing}
          <div class="fil-hero-intro__content">
            <slot></slot>
          </div>
        </div>
      </div>`;

      return this.renderGrid(content);
    }

    const content = html`<div class="cell"><slot name="card-group"></slot></div>`;
    return this.renderGrid(content);
  }

  renderMajorCard() {
    const isHeroPage = this.variation === 'page';
    const noImage = isEmpty(this.image);
    if (isHeroPage && noImage) {
      return nothing;
    }
    const imageHeightCls = {
      'image-height': true,
      'image-height--small': isHeroPage && !noImage,
      'image-height--no-image': isHeroPage && noImage,
      'image-height--auto': true,
      'fil-hero-card-wrapper--cards': this.hasCards,
    };

    if (isHeroPage) {
      const content = html` <div class="cell${classMap(imageHeightCls)}"></div> `;

      return this.renderGrid(content);
    }

    const content = html`
      <div class="cell fil-hero-card-wrapper ${classMap(imageHeightCls)}">
        <div class="fil-hero-card">
          ${this.title ? html`<h1 class="fil-hero-card__title">${this.title}</h1>` : nothing}
          <div class="fil-hero-card__content">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
    return this.renderGrid(content);
  }

  renderImage() {
    let styles = {};
    const noImage = isEmpty(this.image);
    const isHeroPage = this.variation === 'page';
    if (!noImage) {
      const imageUsed = isMobile() ? (this.smallImage ? this.smallImage : this.image) : this.image;
      styles = {
        backgroundImage: `url(${imageUsed})`,
        position: 'absolute',
      };
    }
    const heroImageKindCls = {
      'fil-hero-image-wrapper': true,
      'fil-hero-image-wrapper--small': !noImage && isHeroPage,
      'fil-hero-image-wrapper--no-image': noImage && isHeroPage,
    };
    // set background color if there is no image
    const bgCls = {
      'fil-hero__image': true,
      'fil-hero__image--grey': noImage,
    };

    return html`<div class=${classMap(heroImageKindCls)}>
      <div class="image-container">
        <div class=${classMap(bgCls)} style=${styleMap(styles)}></div>
        <div class="fil-parallelogram">&nbsp;</div>
        ${isHeroPage && noImage
          ? nothing
          : html`<div class="fil-parallelogram-container--right">
              <div class="fil-parallelogram">&nbsp;</div>
              <div class="fil-parallelogram-last">&nbsp;</div>
            </div>`}
      </div>
    </div>`;
  }

  render() {
    return html`
      <div class="fil-hero-wrapper">
        <section class="fil-hero">${this.renderImage()} ${this.renderMajorCard()}</section>
        ${this.renderSlot()}
      </div>
    `;
  }
}

export default FilHero;
